import {
    type Department,
    type TimeEntry,
    TimeEntryType,
    type Venue,
    formatDateShort,
    formatNumber,
    getAllDepartmentPositions,
    getDateArray,
    getTimeResult,
    makeEmptyTimeResult,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import type { SeriesRow } from "@pentacode/ui/charts";
import { chartSeriesColors, formatCurrency, lineChart } from "@pentacode/ui/charts";
import "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { guard } from "lit/directives/guard.js";
import { app } from "../../../init";
import { flatCostEdges } from "../lib/cost-edges";
import { type ColWindow, renderDayWindow } from "../lib/day-window";

@customElement("ptc-roster-costs")
export class RosterCosts extends LitElement {
    @property({ attribute: false })
    from: DateString;

    @property({ attribute: false })
    to: DateString;

    @property({ attribute: false })
    venue: Venue;

    @property({
        attribute: false,
        hasChanged: (value: Department[], oldValue: Department[]) =>
            !oldValue || value.length !== oldValue.length || value.some((d, i) => d.id !== oldValue[i].id),
    })
    departments: Department[] = [];

    @property({ attribute: false })
    entries: TimeEntry[] = [];

    @property({ attribute: false })
    colWindow: ColWindow | null = null;

    @property({ type: Number })
    colWidth = 0;

    private get _dates() {
        return getDateArray(this.from, this.to);
    }

    @state()
    private _costsData: { planned: number; final: number; rows: SeriesRow<DateString>[] } | null = null;

    @state()
    private _gutters = { header: 0, balances: 0 };

    private _gutterObserver = new ResizeObserver(() => this._measureGutters());

    connectedCallback() {
        super.connectedCallback();
        if (this.hasUpdated) {
            this._observeGutters();
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._gutterObserver.disconnect();
    }

    firstUpdated() {
        this._observeGutters();
    }

    updated(changes: Map<string, unknown>) {
        if (
            changes.has("venue") ||
            changes.has("from") ||
            changes.has("to") ||
            changes.has("entries") ||
            changes.has("departments")
        ) {
            this.updateConfig();
        }
    }

    createRenderRoot() {
        return this;
    }

    private _observeGutters() {
        for (const gutter of this.querySelectorAll(".costs-total, .balance-before")) {
            this._gutterObserver.observe(gutter);
        }
    }

    private _measureGutters() {
        const header = (this.querySelector(".costs-total") as HTMLDivElement)?.offsetWidth || 0;
        const balances = (this.querySelector(".balance-before") as HTMLDivElement)?.offsetWidth || 0;
        if (header !== this._gutters.header || balances !== this._gutters.balances) {
            this._gutters = { header, balances };
        }
    }

    private _chart(rows: SeriesRow<DateString>[]) {
        const { header, balances } = this._gutters;
        return lineChart(rows, {
            guides: false,
            grouped: true,
            labels: app.settings.rosterDisplayMode !== "minimal",
            margin: { left: header + balances, right: balances, top: 10, bottom: 10 },
            edges: flatCostEdges(rows, this.from, this.to),
            format: formatCurrency,
            xFormat: (value) => formatDateShort(value as DateString),
            colors: { Geplant: chartSeriesColors[0], Geleistet: chartSeriesColors[2] },
        });
    }

    updateConfig() {
        const dates = this._dates;

        const dailyCosts = new Map<DateString, { planned: number; final: number }>();

        for (const date of dates) {
            const entries = this.entries.filter((e) => {
                const position = e.position || app.getEmployee(e.employeeId)?.positions?.[0];
                return (
                    position &&
                    e.date === date &&
                    !e.deleted &&
                    this.departments.some((d) => getAllDepartmentPositions(d).some((p) => p.id === position.id))
                );
            });

            // We need to recalculate results since they might have changed after editing an entry
            // and before reloading all data. We skip all entries other than work entries since
            // those can not be edited directly in the roster without reloading plus those entries
            // may require the previous averages to calculate absence durations.
            const getResults = (mode: "final" | "planned") =>
                entries
                    .filter((e) => e.employeeId && !e.deleted)
                    .map((e) => {
                        const employee = app.getEmployee(e.employeeId);
                        if (!employee) {
                            return makeEmptyTimeResult();
                        }
                        if (e.type === TimeEntryType.Work) {
                            return getTimeResult(
                                app.company.settings,
                                app.company,
                                employee,
                                e,
                                makeEmptyTimeResult(),
                                1,
                                mode
                            );
                        } else {
                            return (mode === "planned" ? e.resultPlanned : e.result) || makeEmptyTimeResult();
                        }
                    });

            const results = getResults("final");
            const plannedResults = getResults("planned");

            dailyCosts.set(date, {
                planned: plannedResults.reduce((total, res) => total + (res.totalCosts || 0), 0),
                final: results.reduce((total, res) => total + (res.totalCosts || 0), 0),
            });
        }

        const rows = [...dailyCosts.entries()].flatMap(([date, { planned, final }]) => [
            { x: date, series: "Geplant", value: planned },
            { x: date, series: "Geleistet", value: final },
        ]);

        const costsPlanned = [...dailyCosts.values()].reduce((total, { planned }) => total + planned, 0);
        const costsFinal = [...dailyCosts.values()].reduce((total, { final }) => total + final, 0);

        this._measureGutters();
        this._costsData = { planned: costsPlanned, final: costsFinal, rows };
    }

    static styles = css`
        .row.costs {
            height: 68px;
        }

        .costs-total {
            position: sticky;
            z-index: 9;
            background: rgba(255, 255, 255, 0.95);
            text-align: center;
            left: 0;
        }

        .costs-total-title {
            font-weight: 600;
        }

        .costs-total-value {
            font-size: var(--font-size-large);
            color: var(--color-highlight);
        }

        .row.costs ptc-ui-chart {
            position: absolute;
            inset: 0;
            width: auto;
            z-index: 0;
            mask-image: linear-gradient(to right, transparent var(--fade-start), black var(--fade-end));
        }
    `;

    render() {
        const { header, balances } = this._gutters;
        return html`
            <div class="row costs">
                <div class="costs-total">
                    <div class="fullbleed center-aligning evenly stretching horizontal layout">
                        <div class="blue">
                            <div class="costs-total-title">Geplant</div>
                            <div class="costs-total-value">${formatNumber(this._costsData?.planned ?? 0, 0)} €</div>
                        </div>

                        <div class="orange">
                            <div class="costs-total-title">Geleistet</div>
                            <div class="costs-total-value">${formatNumber(this._costsData?.final ?? 0, 0)} €</div>
                        </div>
                    </div>
                </div>
                <div class="balance-before" ?hidden=${!app.settings.rosterDisplayTimeBalances}>
                    <div><ptc-ui-icon name="icon.time-entry.adjustment"></ptc-ui-icon></div>
                </div>
                ${renderDayWindow(
                    this._dates,
                    this.colWindow,
                    this.colWidth,
                    () => html` <div class="employee-day"></div> `
                )}
                <div class="balance-after" ?hidden=${!app.settings.rosterDisplayTimeBalances}>
                    <div><ptc-ui-icon name="icon.time-entry.adjustment"></ptc-ui-icon></div>
                </div>
                ${guard([this._costsData, this._gutters], () =>
                    this._costsData
                        ? html`<ptc-ui-chart
                              label="Personalkosten pro Tag: geplant und geleistet"
                              fill
                              style="--fade-start: ${header}px; --fade-end: ${header + balances}px"
                              .definition=${this._chart(this._costsData.rows)}
                          ></ptc-ui-chart>`
                        : nothing
                )}
            </div>
        `;
    }
}
